import { InvoicePdfAttachmentEnum, InvoicePdfEnum } from '@wilson/interfaces';

export enum BillingFormInvoicePdfType {
  INVOICE = 'invoice',
  ATTACHMENT = 'attachment',
}

export enum BillingFormAttachmentGroupingType {
  GROUPED_BY_USER = 'groupedByUser',
  NOT_GROUPED_BY_USER = 'notGroupedByUser',
  GROUPED_BY_ACTIVITY = 'groupedByActivity',
  GROUPED_BY_SHIFT = 'groupedByShift',
}

export enum BillingFormAttachmentShiftInfoType {
  FULL_SHIFT_INFO = 'fullShiftInfo',
  MIN_SHIFT_INFO = 'minimalShiftInfo',
}

export enum BillingFormAttachmentActivityInfoType {
  BASED_ON_ACTIVITY = 'basedOnActivity',
  BASED_ON_ACTIVITY_GROUPED_BY_SHIFT = 'basedOnActivityGroupedByShift',
}

const PDF_TYPE_TO_INPUT: Record<InvoicePdfEnum, BillingFormInvoicePdfType[]> = {
  [InvoicePdfEnum.ONLY_INVOICE]: [BillingFormInvoicePdfType.INVOICE],
  [InvoicePdfEnum.ONLY_INVOICE_ATTACHMENT]: [
    BillingFormInvoicePdfType.ATTACHMENT,
  ],
  [InvoicePdfEnum.INVOICE_WITH_ATTACHMENT]: [
    BillingFormInvoicePdfType.INVOICE,
    BillingFormInvoicePdfType.ATTACHMENT,
  ],
};

export function transformPdfTypesToUserInput(
  invoicePdfEnum: InvoicePdfEnum,
): BillingFormInvoicePdfType[] {
  return (
    PDF_TYPE_TO_INPUT[invoicePdfEnum] ??
    PDF_TYPE_TO_INPUT[InvoicePdfEnum.INVOICE_WITH_ATTACHMENT]
  );
}

export function transformUserInputToPdfType(
  userInputPdfTypes: BillingFormInvoicePdfType[],
): InvoicePdfEnum {
  const [first] = userInputPdfTypes;
  if (userInputPdfTypes.length === 1) {
    if (first === BillingFormInvoicePdfType.INVOICE)
      return InvoicePdfEnum.ONLY_INVOICE;
    if (first === BillingFormInvoicePdfType.ATTACHMENT)
      return InvoicePdfEnum.ONLY_INVOICE_ATTACHMENT;
  }
  return InvoicePdfEnum.INVOICE_WITH_ATTACHMENT;
}

const ATTACHMENT_TYPE_TO_INPUT: Record<
  InvoicePdfAttachmentEnum,
  {
    attachmentGrouping: BillingFormAttachmentGroupingType;
    attachmentShiftInfo: BillingFormAttachmentShiftInfoType | null;
    attachmentActivityInfo: BillingFormAttachmentActivityInfoType | null;
  }
> = {
  [InvoicePdfAttachmentEnum.BASED_ON_ACTIVITY]: {
    attachmentGrouping: BillingFormAttachmentGroupingType.GROUPED_BY_ACTIVITY,
    attachmentShiftInfo: null,
    attachmentActivityInfo:
      BillingFormAttachmentActivityInfoType.BASED_ON_ACTIVITY,
  },
  [InvoicePdfAttachmentEnum.BASED_ON_ACTIVITY_GROUPED_BY_SHIFT]: {
    attachmentGrouping: BillingFormAttachmentGroupingType.GROUPED_BY_ACTIVITY,
    attachmentShiftInfo: null,
    attachmentActivityInfo:
      BillingFormAttachmentActivityInfoType.BASED_ON_ACTIVITY_GROUPED_BY_SHIFT,
  },
  [InvoicePdfAttachmentEnum.BASED_ON_SHIFT]: {
    attachmentGrouping: BillingFormAttachmentGroupingType.GROUPED_BY_SHIFT,
    attachmentShiftInfo: null,
    attachmentActivityInfo: null,
  },
  [InvoicePdfAttachmentEnum.NOT_GROUPED_BY_USER_WITH_MIN_SHIFT_INFO]: {
    attachmentGrouping: BillingFormAttachmentGroupingType.NOT_GROUPED_BY_USER,
    attachmentShiftInfo: BillingFormAttachmentShiftInfoType.MIN_SHIFT_INFO,
    attachmentActivityInfo: null,
  },
  [InvoicePdfAttachmentEnum.NOT_GROUPED_BY_USER_WITH_FULL_SHIFT_INFO]: {
    attachmentGrouping: BillingFormAttachmentGroupingType.NOT_GROUPED_BY_USER,
    attachmentShiftInfo: BillingFormAttachmentShiftInfoType.FULL_SHIFT_INFO,
    attachmentActivityInfo: null,
  },
  [InvoicePdfAttachmentEnum.GROUPED_BY_USER_WITH_MIN_SHIFT_INFO]: {
    attachmentGrouping: BillingFormAttachmentGroupingType.GROUPED_BY_USER,
    attachmentShiftInfo: BillingFormAttachmentShiftInfoType.MIN_SHIFT_INFO,
    attachmentActivityInfo: null,
  },
  [InvoicePdfAttachmentEnum.GROUPED_BY_USER_WITH_FULL_SHIFT_INFO]: {
    attachmentGrouping: BillingFormAttachmentGroupingType.GROUPED_BY_USER,
    attachmentShiftInfo: BillingFormAttachmentShiftInfoType.FULL_SHIFT_INFO,
    attachmentActivityInfo: null,
  },
};

export function transformAttachmentTypeToUserInput(
  invoicePdfAttachmentEnum: InvoicePdfAttachmentEnum,
) {
  return (
    ATTACHMENT_TYPE_TO_INPUT[invoicePdfAttachmentEnum] ??
    ATTACHMENT_TYPE_TO_INPUT[
      InvoicePdfAttachmentEnum.GROUPED_BY_USER_WITH_FULL_SHIFT_INFO
    ]
  );
}

export function transformUserInputToAttachmentType(
  attachmentGrouping: BillingFormAttachmentGroupingType,
  attachmentShiftInfo: BillingFormAttachmentShiftInfoType | null,
  attachmentActivityInfo: BillingFormAttachmentActivityInfoType | null,
): InvoicePdfAttachmentEnum {
  switch (attachmentGrouping) {
    case BillingFormAttachmentGroupingType.NOT_GROUPED_BY_USER:
      return attachmentShiftInfo ===
        BillingFormAttachmentShiftInfoType.MIN_SHIFT_INFO
        ? InvoicePdfAttachmentEnum.NOT_GROUPED_BY_USER_WITH_MIN_SHIFT_INFO
        : InvoicePdfAttachmentEnum.NOT_GROUPED_BY_USER_WITH_FULL_SHIFT_INFO;

    case BillingFormAttachmentGroupingType.GROUPED_BY_USER:
      return attachmentShiftInfo ===
        BillingFormAttachmentShiftInfoType.MIN_SHIFT_INFO
        ? InvoicePdfAttachmentEnum.GROUPED_BY_USER_WITH_MIN_SHIFT_INFO
        : InvoicePdfAttachmentEnum.GROUPED_BY_USER_WITH_FULL_SHIFT_INFO;

    case BillingFormAttachmentGroupingType.GROUPED_BY_SHIFT:
      return InvoicePdfAttachmentEnum.BASED_ON_SHIFT;

    default:
      return attachmentActivityInfo ===
        BillingFormAttachmentActivityInfoType.BASED_ON_ACTIVITY_GROUPED_BY_SHIFT
        ? InvoicePdfAttachmentEnum.BASED_ON_ACTIVITY_GROUPED_BY_SHIFT
        : InvoicePdfAttachmentEnum.BASED_ON_ACTIVITY;
  }
}
